import { ChevronDown, HelpCircle, LifeBuoy, Mail } from "lucide-react";
import { createFileRoute } from "@tanstack/react-router";
import { msg } from "@lingui/core/macro";
import { useLingui } from "@lingui/react/macro";
import type { MessageDescriptor } from "@lingui/core";

import {
  CompanionHero,
  SettingsPanel,
  SurfaceHeader,
  SurfaceLayout,
  cn,
} from "@ando/ui";

/**
 * ``/settings/help`` — help center surface.
 *
 * Two blocks:
 *   1. FAQ accordion (native ``<details>`` — DRY with TemplateEditor /
 *      ModeVersionHistoryDialog patterns; no Radix Accordion in @ando/ui)
 *   2. Contact support — email mailto with prefilled subject
 *
 * (The external-docs card was removed Jul 2026 — no public docs site
 * exists yet; reintroduce it when one ships.)
 *
 * All copy flows through Lingui (sourceLocale it) — Jul 2026 conversion;
 * the rest of the settings shell is still hardcoded-IT and should follow.
 */
function HelpCenterPage() {
  const { i18n } = useLingui();
  return (
    <SurfaceLayout
      header={
        <SurfaceHeader
          label={i18n._(msg`Aiuto`)}
          title={i18n._(msg`Help center`)}
          meta={<span>{i18n._(msg`FAQ · contatti supporto`)}</span>}
        />
      }
      hero={
        <CompanionHero icon={<LifeBuoy size={14} strokeWidth={2} />}>
          {i18n._(
            msg`Hai una domanda veloce? Inizia dalle FAQ. Per casi specifici scrivi al supporto — risponde una persona, non un bot.`,
          )}
        </CompanionHero>
      }
    >
      <div className="flex flex-col gap-6 pt-2">
        <FaqSection />
        <SupportCard />
      </div>
    </SurfaceLayout>
  );
}

// ── FAQ ─────────────────────────────────────────────────────────────────

interface Faq {
  readonly question: MessageDescriptor;
  readonly answer: MessageDescriptor;
}

const FAQS: ReadonlyArray<Faq> = [
  {
    question: msg`Come collego un connettore?`,
    answer: msg`Dalla spalla utente apri Connettori (o vai su /workspace/integrations). Scegli il vendor, autorizza l'accesso e attendi la prima sincronizzazione. I dati compaiono nelle tab Workspace e diventano citabili in chat.`,
  },
  {
    question: msg`Come funziona la fatturazione?`,
    answer: msg`Trovi piano, utilizzo e ricevute in Impostazioni → Fatturazione. Le risorse (messaggi, ingestion, archiviazione) si rinnovano ad inizio mese. Se ti avvicini al limite riceverai un nudge in chat prima del blocco.`,
  },
  {
    question: msg`Cosa sono i Modi agente?`,
    answer: msg`Profili che definiscono come Ando risponde: focus (testo / dati strutturati / full), accesso al web, pensiero esteso. Puoi crearne di personalizzati o usare quelli condivisi dall'organizzazione. Si selezionano dal composer.`,
  },
  {
    question: msg`Posso invitare colleghi?`,
    answer: msg`Sì — apri Impostazioni → Organizzazione e usa Invita membri. Servono i ruoli appropriati (admin per inviti). Ogni membro vede solo i dati a cui ha accesso secondo le policy condivise dall'organizzazione.`,
  },
  {
    question: msg`Come elimino i miei dati?`,
    answer: msg`Da Impostazioni → Privacy & dati puoi esportare l'archivio completo o richiedere l'eliminazione dell'account. L'eliminazione è irreversibile e propaga su ingestion, embeddings e backup entro 30 giorni.`,
  },
  {
    question: msg`Perché alcuni messaggi sono più lenti di altri?`,
    answer: msg`Quando attivi Focus, web search o pensiero esteso, Ando esegue più passi di ricerca e verifica prima di rispondere. Per risposte rapide disattiva queste opzioni dal pannello strumenti del composer.`,
  },
  {
    question: msg`I miei dati allenano modelli esterni?`,
    answer: msg`No. Le conversazioni e i dati dei tuoi connettori non vengono usati per addestrare modelli di terze parti. Maggiori dettagli su privacy, retention e log access trail in Impostazioni → Privacy & dati.`,
  },
];

function FaqSection() {
  const { i18n } = useLingui();
  return (
    <SettingsPanel
      aria-labelledby="help-faq-heading"
      icon={HelpCircle}
      title={<span id="help-faq-heading">{i18n._(msg`Domande frequenti`)}</span>}
    >
      <ul className="flex flex-col gap-1">
        {FAQS.map((faq, idx) => (
          <li key={faq.question.id}>
            <details
              className={cn(
                "group rounded-md border border-transparent transition-colors",
                "open:border-hairline open:bg-card/40",
                idx > 0 && "mt-1",
              )}
            >
              <summary
                className={cn(
                  "flex cursor-pointer list-none items-center justify-between gap-3 rounded-md px-3 py-2.5",
                  "font-medium text-[13px] text-ink-1",
                  "hover:bg-black/[0.03]",
                  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ando-orange/60",
                )}
              >
                <span className="min-w-0 flex-1">{i18n._(faq.question)}</span>
                <ChevronDown
                  size={14}
                  strokeWidth={1.75}
                  aria-hidden
                  className="shrink-0 text-ink-3 transition-transform group-open:rotate-180"
                />
              </summary>
              <p className="max-w-prose px-3 pt-1 pb-3 text-[12.5px] text-ink-2 leading-[1.55]">
                {i18n._(faq.answer)}
              </p>
            </details>
          </li>
        ))}
      </ul>
    </SettingsPanel>
  );
}

// ── Support card ────────────────────────────────────────────────────────

const SUPPORT_EMAIL = "support@ando-ai.com";

function SupportCard() {
  const { i18n } = useLingui();
  const supportHref = `mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent(
    i18n._(msg`Ando — richiesta supporto`),
  )}`;
  const bugHref = `mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent(
    i18n._(msg`Ando — bug critico (URGENTE)`),
  )}`;

  return (
    <SettingsPanel
      aria-labelledby="help-support-heading"
      icon={Mail}
      title={<span id="help-support-heading">{i18n._(msg`Contatta il supporto`)}</span>}
    >
      <p className="mb-3 max-w-prose text-[12.5px] text-ink-2 leading-[1.55]">
        {i18n._(
          msg`Risponde una persona, non un bot. Includi una breve descrizione e — se possibile — uno screenshot o l'ID conversazione.`,
        )}
      </p>
      <div className="flex flex-wrap items-center gap-2">
        <a
          href={supportHref}
          className={cn(
            "inline-flex h-9 items-center gap-2 rounded-md bg-ink-1 px-3 font-medium text-[12.5px] text-white",
            "hover:bg-ink-1/90",
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ando-orange/60 focus-visible:ring-offset-1",
          )}
        >
          <Mail size={13} strokeWidth={1.75} aria-hidden />
          {i18n._(msg`Scrivi a ${SUPPORT_EMAIL}`)}
        </a>
        <a
          href={bugHref}
          className={cn(
            "inline-flex h-9 items-center gap-2 rounded-md border border-error/30 bg-error/5 px-3 font-medium text-[12.5px] text-error",
            "hover:bg-error/10",
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-error/40",
          )}
        >
          {i18n._(msg`Segnala un bug critico`)}
        </a>
      </div>
      <p className="mt-3 text-[11px] text-ink-3">
        {i18n._(msg`Rispondiamo entro 24 ore lavorative.`)}
      </p>
    </SettingsPanel>
  );
}

export const Route = createFileRoute("/_authed/_modal/settings/help")({
  component: HelpCenterPage,
});
